import * as React from "react";
import {useEffect} from "react";
import {translate} from "../../translate/translate";
import {
    Box,
    Button,
    Fab,
    FormControl,
    Grid,
    InputAdornment,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../components/table/table-pagination.component";
import {Add, Search} from "@mui/icons-material";
import {toast} from "react-toastify";
import {PaginatedResult} from "../../models/default/paginated-result";
import TableActionComponent from "../../components/table/table-action.component";
import {Field, Form, Formik, FormikHelpers} from "formik";
import {ErrorComponent} from "../../components/error/error.component";
import GenericDialog from "../../components/dialog/generic-dialog.component";
import * as Yup from "yup";
import {Category} from "../../models/basic/category";
import {CategoryFilter, CategoryService} from "../../services/category-service";
import {useFilters} from "../../filters/base-filter";
import {Project} from "../../models/basic/project";
import {usePagination} from "../../utils/pagination";


const CategoryComponent = () => {
    const service = new CategoryService();
    const {filters, updateFilter, updateFilterKeepingParam, resetFilters } = useFilters<CategoryFilter>(
        { search: "", active: ""}, ["search", "active"]
    );

    const [categoryList, setCategoryList] = React.useState<PaginatedResult<Category>>();

    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<Category>(categoryList);

    const url = "/registration/category/";

    const [object, setObject] = React.useState(new Category());
    const [openDialog, setOpenDialog] = React.useState(false);
    const [invalidForm, setInvalidForm] = React.useState(true);
    const [isEdit, setIsEdit] = React.useState(false);

    const schema = Yup.object().shape({
        name: Yup.string()
            .required(),
        description: Yup.string().required(),
    });

    const handleOpenEdit = (row: Category) => {
        setIsEdit(true);
        setObject(row);
        setInvalidForm(false);
        setOpenDialog(true);
    }

    const handleOpenNew = () => {
        setIsEdit(false);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setInvalidForm(true);
        setOpenDialog(false);
        setObject(new Category());
    };

    const search = (restartIndex = false) => {
        service.readPaginated(filters, [], pagination).then(response => {
            setCategoryList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });

        restartIndex && resetPage();
    };

    useEffect(() => {
        search();
    }, [pagination]);

    const handleDelete = (row: Project) => {
        service.delete(Number(row.id)).then(() => {
            toast.success(translate.t("successfully_deleted"));
            resetFilters();
            resetPage();
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    const handleSwitch = (row: Project, column: keyof Project) => {
        (row[column] as boolean) = !row[column];
        const payload = {"active": row.active};
        service.patch(Number(row.id), payload).then(() => {
            toast.success(translate.t("successfully_updated"));
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    const handleSubmit = (values: Category) => {
        setTimeout(() => {
            const category = new Category();
            const formattedValues = {
                ...values,
                name: values.name.trim(),
                description: values.description?.trim(),
            };
            Object.assign(category, formattedValues);
            if (category.id !== undefined) {
                service.patch(category.id, category).then(() => {
                    toast.success(translate.t("successfully_updated"));
                    handleCloseDialog();
                    search(true);
                }).catch((error: any) => {
                    ErrorComponent(error);
                });
            } else {
                const { id: _, ...newObj } = category;
                service.create(newObj as any)
                    .then(() => {
                        toast.success(translate.t("successfully_registered"));
                        handleCloseDialog();
                        search(true);
                    })
                    .catch((error: any) => {
                        ErrorComponent(error);
                    });
            }
        }, 500);
    }

    const handleObjChange = (values: Category) => {
        setObject(values);
        setInvalidForm(!values.name.trim() || !values.description?.trim());
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("categories")}
                </Typography>
            </div>
            <div className="floating-button">
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Add/>}
                    onClick={() => {
                        handleOpenNew();
                    }}>{translate.t("new_category").toUpperCase()}</Button>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Grid alignItems="center" container spacing={2} direction={"row"} width="50%">
                                            <Grid size={5}>
                                                <Field
                                                    as={TextField}
                                                    name="search"
                                                    label={""}
                                                    fullWidth
                                                    variant={"outlined"}
                                                    autoFocus={true}
                                                    placeholder={translate.t("search_category")}
                                                    value={filters.search}
                                                    onChange={(event: React.ChangeEvent<{
                                                        value: unknown
                                                    }>) => updateFilterKeepingParam("search", event.target.value)}
                                                />
                                            </Grid>
                                            <Grid >
                                                <Fab
                                                    size="small"
                                                    type="submit"
                                                    color="secondary">
                                                    <Search/>
                                                </Fab>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["category", "description", "Status", "action"]}
                    displayedColumns={["name", "description", "active", "action"]}
                    handleDelete={handleDelete}
                    handleSwitch={handleSwitch}
                    editWithDialog={handleOpenEdit}
                    url={url}
                    handleDoubleClick={handleOpenEdit}
                    count={categoryList?.count}
                    next={categoryList?.next}
                    previous={categoryList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    actionsComponent={TableActionComponent}
                    columnStyles={{1:"35%", 2:"35%", 3:"15%", 4:"15%"}}
                    showActions={["edit", "delete"]}
                />
            </Paper>
            <GenericDialog
                open={openDialog}
                onClose={handleCloseDialog}
                title={isEdit ? translate.t("edit_category") : translate.t("new_category")}
                colorButton={"primary"}
                labelButton={"save"}
                disableConfirm={invalidForm}
                handleConfirm={() => handleSubmit(object)}>
                <Box>
                    <FormControl fullWidth>
                        <Formik
                            id="dialog-form"
                            enableReinitialize
                            initialValues={object}
                            validationSchema={schema}
                            onSubmit={(values: Category, {resetForm}: FormikHelpers<Category>) => {
                                handleSubmit(values);
                                resetForm();
                                handleCloseDialog();
                            }}
                            validateOnChange={true}>
                            {({isValid, values, isValidating, errors, touched}) => (
                                <Form autoComplete="off">
                                    <Grid container spacing={2} direction={"column"}>
                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="name"
                                                label={translate.t("name")}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={true}
                                                required={true}
                                                values={values}
                                                error={touched.name && Boolean(errors.name) && invalidForm}
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    if (value.length > 256) return;
                                                    handleObjChange({
                                                        ...obj,
                                                        name: value as string,
                                                    });
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.name && Boolean(errors.name) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>
                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="description"
                                                label={translate.t("description")}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={false}
                                                required={true}
                                                values={values}
                                                error={touched.description && Boolean(errors.description) && invalidForm}
                                                minRows={4}
                                                multiline
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    if (value.length > 256) return;
                                                    handleObjChange({
                                                        ...obj,
                                                        description: value as string,
                                                    })
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.description && Boolean(errors.description) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </FormControl>
                </Box>
            </GenericDialog>
        </section>
    );
};

export default CategoryComponent;
